import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { store } from 'components/Store';
import useRegulationDocumentData from 'components/Dashboard/Regulations/useRegulationDocumentData';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import RegulationsDocumentOverviewTab from 'components/Dashboard/Regulations/RegulationsDocumentOverviewTab';
import RegulationsDocumentCommentsTab from 'components/Dashboard/Regulations/RegulationsDocumentCommentsTab';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import ThumbsDown from 'components/Common/ThumbsDown';
import Share from 'components/Dashboard/components/Share';
import time from 'utils/time';

const TAB_HASH = {
  overview: 'overview',
  comments: 'comments',
};

const tabFromHash = () => {
  const key = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '');
  return TAB_HASH[key] ? key : 'overview';
};

const RegulationDocumentContentPage = ({ currentNew, contentContainer, hideSummary }) => {
  const { dispatch } = useContext(store);
  const navigate = useNavigate();
  const location = useLocation();
  const { comments, commentsTotal, isCommentsLoading, loadRegulationDocumentComments } = useRegulationDocumentData({
    commentsOnly: true,
  });
  const [activeTab, setActiveTab] = useState(tabFromHash);
  const [heightContainer, containerRef] = useHeightContainer();
  const [scrollingOverview, setScrollingOverview] = useState(false);
  const scrollbarRef = useRef(null);
  const titleSection = useRef(null);
  const { showFixedTitle, titleStyles } = useTitleScrolledStyles({ contentContainer, titleSection, leftValue: 0 });

  useEffect(() => {
    setActiveTab(tabFromHash());
  }, [location.hash]);

  const selectTab = (tab) => {
    navigate({ hash: TAB_HASH[tab] }, { replace: true });
  };

  const { createMarkup } = useCreateMarkup();
  const commentsTitle = `Comments${commentsTotal > 0 ? ` (${commentsTotal})` : ''}`;
  const { id, title, objectId, objectType, canonicalUrl } = currentNew ?? {};
  const item = { id, title, objectID: objectId, type: objectType };

  return (
    <>
      <div className='section-title pb-2 regulations-content-title p-3 p-lg-4' ref={titleSection}>
        <h3 className='main-title news-title pr-lg-4' dangerouslySetInnerHTML={createMarkup(currentNew?.title)} />
        <div className='row mx-0 justify-content-between align-items-center'>
          <p className='heading-date mb-0'>
            {!!currentNew?.agency && (
              <>
                {currentNew.agency}
                <span className='dot'></span>
              </>
            )}
            {!!currentNew?.documentType && (
              <>
                {currentNew.documentType}
                <span className='dot'></span>
              </>
            )}
            {!!currentNew?.postedDate && (
              <span className='date'>{time(currentNew.postedDate).formatGeneralDate()}</span>
            )}
            {!!currentNew?.sourceUrl && (
              <>
                <span className='dot' />
                <a
                  className='legislation-source mb-0'
                  href={currentNew.sourceUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Go to source
                </a>
              </>
            )}
          </p>
          <div className='flex-centered'>
            <ThumbsDown
              item={{ objectID: currentNew?.objectId }}
              nameSection='RegulationDocuments'
              show={!!currentNew?.keywordsFromLists}
              contentPage={true}
              alreadyMarked={!!currentNew?.feedback}
              keywordsFromLists={currentNew?.keywordsFromLists}
              thick={true}
            />
            <Share
              item={item}
              id={id}
              title={title}
              link={canonicalUrl ? `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}` : undefined}
            />
          </div>
        </div>
      </div>
      <div className='px-3 px-lg-4 pt-4 border-bottom' ref={containerRef}>
        <div className='mb-4'>
          {currentNew?.regulationDocketId ? (
            <Link
              to={`/regulation-docket/${currentNew.regulationDocketId}/${normalize(currentNew.docketTitle)}`}
              className='general-button hovered-button border rounded-pill px-3 py-1 mr-3 d-inline-block reset-link link-main-blue'
              onClick={() => dispatch({ type: 'MODIFY_SECTION', parameter: 'clickedOnContentUrl', value: null })}
            >
              Go to docket
            </Link>
          ) : (
            <button className='general-button hovered-button border rounded-pill px-3 py-1 mr-3' disabled>
              Go to docket
            </button>
          )}
          <button
            className={`general-button ${activeTab === 'overview' ? 'action-button' : 'hovered-button'} border rounded-pill px-3 py-1 mr-3`}
            onClick={() => selectTab('overview')}
          >
            Document overview
          </button>
          <button
            className={`general-button ${activeTab === 'comments' ? 'action-button' : 'hovered-button'} border rounded-pill px-3 py-1`}
            onClick={() => selectTab('comments')}
          >
            {commentsTitle}
          </button>
        </div>
      </div>
      <div
        className={`content-item-scrolled-title main-sticky section-title bg-white parliamentary-scrolled-title ${
          showFixedTitle ? 'item-scrolled-title-visible' : ''
        }`}
        style={titleStyles}
      >
        <div className='d-flex justify-content-between scrolled-title-container'>
          <h3
            className='main-title news-title bg-white content-item-title'
            dangerouslySetInnerHTML={createMarkup(currentNew?.title)}
          />
          <ThumbsDown
            item={{ objectID: currentNew?.objectId }}
            nameSection='RegulationDocuments'
            show={!!currentNew?.keywordsFromLists}
            contentPage={true}
            alreadyMarked={!!currentNew?.feedback}
            keywordsFromLists={currentNew?.keywordsFromLists}
            thick={true}
          />
          <Share
            item={item}
            id={id}
            title={title}
            link={canonicalUrl ? `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}` : undefined}
          />
        </div>
      </div>
      {activeTab === 'overview' && !!currentNew && (
        <CustomScrollbar
          className={'simple-scrollbar indented-scrollbar-small'}
          style={{ height: `${heightContainer - 95}px` }}
          minimalThumbYSize={55}
          maximalThumbYSize={100}
          onScrollStart={() => {
            setScrollingOverview(true);
          }}
          onScrollStop={() => {
            setScrollingOverview(false);
          }}
        >
          <RegulationsDocumentOverviewTab
            currentNew={currentNew}
            scrollbarRef={scrollbarRef}
            scrolling={scrollingOverview}
          />
        </CustomScrollbar>
      )}
      {activeTab === 'comments' && (
        <RegulationsDocumentCommentsTab
          comments={comments}
          commentsTotal={commentsTotal}
          isLoading={isCommentsLoading}
          loadRegulationDocumentComments={loadRegulationDocumentComments}
          heightContainer={heightContainer}
        />
      )}
    </>
  );
};

export default RegulationDocumentContentPage;
